import { ArrowLeftOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Form, Input, Select, Switch, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getIndustryChoices, updateCompany } from 'api/companies';
import { updateCustomer } from 'api/customers';
import AddCustomerToCompanyModal from './AddCustomerToCompanyModal';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import { priceTransformer } from 'utils';
import { App } from 'antd';

function OverviewTab({ company, setCompany }) {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [industryChoices, setIndustryChoices] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { message } = App.useApp();

  useEffect(() => {
    getIndustryChoices()
      .then((res) => {
        const data = getData(res)?.data ?? getData(res);
        const choices = data?.choices ?? [];
        setIndustryChoices(Array.isArray(choices) ? choices : []);
      })
      .catch(() => setIndustryChoices([]));
  }, []);

  React.useEffect(() => {
    if (company) {
      form.setFieldsValue({
        name: company?.name ?? '',
        industry: company?.industry ?? '',
        website: company?.website ?? '',
        notes: company?.notes ?? '',
        is_active: company?.is_active ?? true,
      });
    }
  }, [company, form]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      updateCompany(company.id, values)
        .then((res) => {
          const data = getData(res)?.data ?? getData(res) ?? res?.data ?? res;
          setCompany((c) => ({ ...c, ...data }));
          setEditing(false);
          message.success('Company updated');
        })
        .catch((e) => {
          captureException(e);
          message.error(getHttpErrorMessage(e));
        })
        .finally(() => setSaving(false));
    }).catch(() => {});
  };

  const handleRemoveCustomer = (customerId) => {
    updateCustomer(customerId, { company: null })
      .then(() => {
        setCompany((c) => ({
          ...c,
          customers: (c.customers ?? []).filter((cust) => cust.id !== customerId),
        }));
        message.success('Customer removed from company');
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      });
  };

  const customerColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v) => v || '-' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v) => v || '-' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v) => v || '-' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => handleRemoveCustomer(record.id)}
        >
          Remove
        </Button>
      ),
    },
  ];

  if (!company) return null;

  return (
    <div style={{ maxWidth: 800 }}>
      <Card
        title="Company Details"
        extra={(
          editing ? (
            <>
              <Button onClick={() => setEditing(false)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        )}
      >
        {editing ? (
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
              <Input placeholder="Company name" />
            </Form.Item>
            <Form.Item name="industry" label="Industry">
              <Select
                placeholder="Select industry"
                allowClear
                showSearch
                optionFilterProp="label"
                options={industryChoices.map((c) => ({ value: c, label: c }))}
              />
            </Form.Item>
            <Form.Item name="website" label="Website">
              <Input placeholder="https://..." type="url" />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={3} placeholder="Notes..." />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Form>
        ) : (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Name">{company.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Industry">{company.industry || '-'}</Descriptions.Item>
            <Descriptions.Item label="Website">
              {company.website ? (
                <a href={company.website} target="_blank" rel="noopener noreferrer">
                  {company.website}
                </a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Notes">{company.notes || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">{company.is_active ? 'Active' : 'Inactive'}</Descriptions.Item>
            <Descriptions.Item label="Total Revenue">{priceTransformer(company.total_revenue ?? 0)}</Descriptions.Item>
            <Descriptions.Item label="Contacts">{company.contact_count ?? 0}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card
        title={(
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            Associated Customers ({(company.customers ?? []).length})
          </span>
        )}
        extra={(
          <Button type="primary" onClick={() => setAddCustomerOpen(true)}>
            Add Customer
          </Button>
        )}
        style={{ marginTop: 24 }}
      >
        <Table
          rowKey="id"
          columns={customerColumns}
          dataSource={company.customers ?? []}
          pagination={false}
          locale={{ emptyText: 'No customers associated. Add customers below or assign from their profile.' }}
        />
      </Card>
      <AddCustomerToCompanyModal
        open={addCustomerOpen}
        company={company}
        onClose={() => setAddCustomerOpen(false)}
        onSuccess={(newCustomer) => {
          setCompany((c) => ({
            ...c,
            customers: [...(c.customers ?? []), newCustomer],
          }));
        }}
      />
    </div>
  );
}

export default OverviewTab;
